/**
 * ManageSubmission component is a crucial part of the student interface for submitting and managing their code submissions.
 * It provides two options for code submission:
 * 1. Uploading a code file.
 * 2. Writing code directly in the integrated code editor.
 *
 * Key Features:
 * - Students can view previous submissions and select one to see its details.
 * - Students can run their code in the editor without submitting it.
 * - Custom input execution is supported to test code with user-provided input.
 * - The component displays compiler errors, test results, and execution status of submissions.
 * - Students can see marks obtained in both visible and hidden test cases.
 * - The interface includes a dropzone for file uploads, a code editor, and various controls for managing submissions.
 * - Supports running code with custom inputs and viewing the output directly.
 * - Provides options to zoom in/out and toggle fullscreen mode for the code editor.
 * - Displays detailed feedback on test results, including marks and any compiler errors.
 */

import log from "loglevel";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Dropzone from "react-dropzone";
import Dropdown from "react-bootstrap/Dropdown";
import Alert from "react-bootstrap/Alert";
import { Card } from "react-bootstrap";
import { AiOutlinePlayCircle, AiOutlineRetweet } from "react-icons/ai";
import { FiSettings, FiMinus } from "react-icons/fi";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import Modal from "react-bootstrap/Modal";
import * as constants from "Constants";
import httpService from 'services/httpService.js';
import { updateSelectedSubmission } from "../../../../../../../Store/Programming/Student/Actions";
import JSZip from "jszip";
import { MdOutlineZoomIn, MdOutlineZoomOut, MdOutlineFullscreen, MdOutlineFullscreenExit } from "react-icons/md";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { VscRunAll } from "react-icons/vsc";
import RunTerminal from "./RunTerminal";
import MonacoEditorComponent from './LazyLoadMonacoEditor';
import { MdDarkMode } from "react-icons/md";
import { MdLightMode } from "react-icons/md";

export default function ManageSubmission({
  programId,
  changeAccess,
  access,
  submission,
  setSelectedSubmission,
  setTestResults,
  instructorAdditionalFilesContent,
  setShowStudentPreview,
  studentPreviewCode,
  setStudentPreviewCode,
  isTemplateCode,
  setIsTemplateCode,
  practiceMarks,
  evaluateMarks
}) {
  // Use the useSelector hook to access Redux state
  const studentProgramming = useSelector((state) => state.studentprogramming);
  // Use the useDispatch hook to get the dispatch function
  const dispatch = useDispatch();
  let interval;

  //States for handling the execution of the code on custom input
  //================================================================================================
  const [showPopupCustomInput, setShowPopupCustomInput] = useState(false); // state for showing custom input window
  const [customInput, setCustomInput] = useState(null); // input for custom input execution
  const [showCustomInputCard, setShowCustomInputCard] = useState(false); // to show custom input card(will be visible when student will make a submission)
  const [isFetchingCustom, setIsFetchingCustom] = useState(false); // to show execution status for CustomInputRun
  let [customResult, setCustomResult] = useState(null); // result for custom input

  //================================================================================================
  //States for handling the execution of the code on test cases
  //================================================================================================
  let evaluate_flag = "Evaluation in Process";

  const [isFetchingVisible, setIsFetchingVisible] = useState(false); // to show loading symbol for run practice marks
  const [isFetchingHidden, setIsFetchingHidden] = useState(false); // to show loading symbol for run hidden marks
  const [evaluationStatus, setEvaluationStatus] = useState("");
  const [obtainedVisibleMarks, setObtainedVisibleMarks] = useState(null);
  const [obtainedHiddenMarks, setObtainedHiddenMarks] = useState(null);
  const [showPopupCompilerError, setShowPopupCompilerError] = useState(false); // state for showing compiler error window
  const [compilerErrors, setCompilerErrors] = useState(null); // compiler errors for the selected submission
  //================================================================================================

  //States for handling file upload
  //================================================================================================
  let [selectedFiles, setSelectedFiles] = useState([]); // files chosen by student to make a submission
  const [fileSize, setFileSize] = useState(true); // states to handle file issues
  const [_fileUploadProgress, setFileUploadProgress] = useState(false);
  const [fileUploadResponse, setFileUploadResponse] = useState(null);
  const [Expected_Submission_File, setExpectedSubmissionFile] = useState(""); // State for storing allowed file names
  const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);
  const [fileWasUploaded, setFileWasUploaded] = useState(false); //State for storing if the file was uploaded
  //================================================================================================

  //States for handling the previous submissions
  //================================================================================================
  const [submissions, setSubmissions] = useState(null); // list of submissions by the student
  const [showPopupSubmissions, setShowPopupSubmissions] = useState(false); // state for showing submission window
  const [selectedPopUpSubmission, setSelectedPopUpSubmission] = useState(null); // selected submission from the pop-up
  const [pageCount, setPageCount] = useState(0); // used in the pagination, it is the total number of pages required to display all the records.
  const [sizePage, setSizePage] = useState(10); // used in the pagination, sizePage is the number of records we want to show per page
  //================================================================================================

  //States for handling the code editor
  //================================================================================================
  const initialEditorText = {};
  const headerFileToBeSubmitted = submission.header_files_to_be_submitted.map(
    (file) => file + ".h"
  );
  //Initiliasing a dictionary with key as file name and value as empty string for the editor
  submission.files_to_be_submitted.forEach((file) => {
    initialEditorText[file] = "";
  });
  //Initiliasing a dictionary with key as header file name and value as empty string for the editor
  headerFileToBeSubmitted.forEach((file) => {
    initialEditorText[file] = "";
  });
  const [language, setLanguage] = useState(submission?.program_language[0]); // default language for editor
  const [editorText, setEditorText] = useState(initialEditorText);
  const [currentFileinEditor, setCurrentFileinEditor] = useState(
    submission.files_to_be_submitted[0]
  );

  const prevEditorTextRef = useRef(editorText);
  // Ref to store the editor instance
  const editorRef = useRef(null);
  // Flag to prevent unnecessary updates to the editor text
  const ignoreChange = useRef(false);

  const languageToFileExtension = {
    C: "c",
    "C++": "cpp",
    Python3: "py",
    Simplecpp: "cpp",
    Java: "java",
  };
  const languageToSyntaxHighlighting = {
    C: "c",
    "C++": "cpp",
    Python3: "python",
    Simplecpp: "cpp",
    Java: "java",
  };

  const [mode, setMode] = useState('light');
  //States for handling full screen and font size of the code editor
  const [fontSize, setFontSize] = useState(14); // Default font size
  const [isFullScreen, setisFullScreen] = useState(false);
  const handle = useFullScreenHandle();

  //States for handling the if the current file in Editor is an instructor file
  const [isInstructorFile, setIsInstructorFile] = useState(false);

  // This array stores files to be submitted and additional files provided by the instructor
  const allFiles = [
    ...submission.files_to_be_submitted,
    ...headerFileToBeSubmitted,
    ...(instructorAdditionalFilesContent[language]
      ? Object.keys(instructorAdditionalFilesContent[language])
      : []),
  ];
  //================================================================================================

  //States for handling auto save feature
  //================================================================================================
  const [sessionKey, setSessionKey] = useState(""); //To save the sessionkey for autosaving
  const [lastSavedTime, setLastSavedTime] = useState(new Date());
  const [showEditorPopup, setShowEditorPopup] = useState(false); // state for showing editor popup

  //================================================================================================
  //States for run without submit feature
  //================================================================================================
  const [errorOnRun, setErrorOnRun] = useState("");
  const [successOnRun, setSuccessOnRun] = useState(false);
  const [runToggle, setRunToggle] = useState(false);
  const [isRunLoading, setIsRunLoading] = useState(false);
  //================================================================================================

  //To catch the ResizeObserver error and hide the error message
  //during exit from Code Editor Full Screen mode.
  useEffect(() => {
    const errorHandler = (e) => {
      if (
        e.message.includes(
          "ResizeObserver loop completed with undelivered notifications" ||
          "ResizeObserver loop limit exceeded"
        )
      ) {
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.style.display = "none";
        }
      }
    };
    window.addEventListener("error", errorHandler);
    return () => {
      window.removeEventListener("error", errorHandler);
    };
  }, []);

  useEffect(() => {
    if (programId) {
      // Construct the URL for fetching the latest submission
      var url = constants.base_url + "/evalpro/get_latest/" + programId;
      httpService
        .get(url, {
          headers: {
            "Content-Type": "application/pdf",
          },
        })
        .then((response) => {
          // Dispatch an action to update the selected submission in the Redux store
          dispatch(
            updateSelectedSubmission(response.data.student_submission.id)
          );

          // Check if the student submission data is not empty
          if (Object.keys(response.data.student_submission).length !== 0) {
            // Set the latest submission as the selected submission
            setSelectedSubmission(response.data.student_submission);
            setSelectedPopUpSubmission(response.data.student_submission);

            // If execution of visible test cases is done, set the obtained marks
            if (
              !response.data.student_submission
                .execution_pending_for_visible_testcases
            ) {
              setObtainedVisibleMarks(
                response.data.student_submission.obtained_visible_marks
              );
            }

            // If execution of hidden test cases is done, set the obtained marks
            if (
              !response.data.student_submission
                .execution_pending_for_hidden_testcases
            ) {
              setObtainedHiddenMarks(
                response.data.student_submission.obtained_hidden_marks
              );
            }
            // Set the compiler errors
            setCompilerErrors(response.data.student_submission.compiler_errors);
          }
        })
        .catch((error) => {
          log.error(error);
        });

      // Construct the URL for fetching the editor data
      url = constants.base_url + "/evalpro/editor/" + programId;

      httpService
        .get(url, {
          headers: {
            "Content-Type": "application/pdf",
          },
        })
        .then((response) => {
          // If the response indicates a failure, it means that the buffer is not present for the student for this activity.
          //In this case, we need to create a new buffer for the student. post request is made to create a new buffer for the student.
          if (response.data.status == "fail") {
            console.log("editor response 1", response.data);
            let form_data = new FormData();
            form_data.append("buffer_data", JSON.stringify(editorText));
            httpService
              .post(url, form_data, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
              .then((response) => {
                // Set the session key from the response
                setSessionKey(response.data.editor_buffer.session_key);
              });
          } else {
            // If the response indicates success, update the editor text with the latest buffer data
            setSessionKey(response.data.editor_buffer.session_key);
            setStudentPreviewCode(response.data.student_preview);
            setShowStudentPreview(true);
            setIsTemplateCode(response?.data?.is_template_coding_activity);
            const updatedEditorText = {
              // Spread the existing 'editorText' object to maintain previous state
              ...editorText,
              // Use 'reduce' to iterate over each key in 'editorText'
              ...Object.keys(editorText).reduce((acc, key) => {
                // If the current key exists in the 'buffer_data' from the server response
                if (key in response.data.editor_buffer.buffer_data) {
                  // Add the key-value pair from the server response to the accumulator object
                  acc[key] = response.data.editor_buffer.buffer_data[key];
                }
                // Return the accumulator object for the next iteration
                return acc;
              }, {}),
            };
            setEditorText(updatedEditorText);
          }
        });
    }

    // Construct the expected submission file names based on the program language and files to be submitted
    let files_to_be_submitted = submission.files_to_be_submitted;
    let program_language = submission.program_language;
    let cppCount = 0;
    let Expected_Submission_FileNames = "";
    let header_files_to_be_submitted = submission.header_files_to_be_submitted;

    for (let i = 0; i < program_language.length; i++) {
      let lang = program_language[i];
      let extension;

      // Determine the file extension based on the program language
      if (lang === "C++" || lang === "Simplecpp") {
        extension = ".cpp";
      } else if (lang === "C") {
        extension = ".c";
      } else if (lang === "Python3") {
        extension = ".py";
      } else if (lang === "Java") {
        extension = ".java";
      }

      if (extension) {
        // Skip the second occurrence of .cpp for the case of Simplecpp
        if (extension === ".cpp") {
          cppCount++;
          if (cppCount === 2) {
            continue;
          }
        }
        // Construct the expected submission file names
        Expected_Submission_FileNames +=
          (i > 0 ? " or " : "") +
          files_to_be_submitted.map((file) => file + extension).join(",");
        if (header_files_to_be_submitted.length > 0) {
          Expected_Submission_FileNames +=
            "," +
            header_files_to_be_submitted.map((file) => file + ".h").join(",");
        }
      }
    }

    // Set the expected submission file names
    setExpectedSubmissionFile(Expected_Submission_FileNames);
  }, []);

  //This useEffect is used to support the auto-save feature in the code editor. This runs whenever the 'editorText' state changes.
  useEffect(() => {
    if (access.enable_upload_submission) {
      const currentTime = new Date();

      // If more than 5 seconds have passed since the last save
      if (currentTime - lastSavedTime > 5000) {
        const url = constants.base_url + "/evalpro/editor/" + programId;
        const form_data = new FormData();
        form_data.append("buffer_data", JSON.stringify(editorText));
        form_data.append("session_key", sessionKey);

        httpService
          .put(url, form_data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            // If the server response indicates that the code has changed
            if (response.data.status === "code_changed") {
              // Show the editor popup
              setShowEditorPopup(true);
            }
            // Update 'prevEditorTextRef' with the current 'editorText' state
            prevEditorTextRef.current = editorText;
          });

        // Update 'lastSavedTime' with the current time
        setLastSavedTime(currentTime);
      }

      // Set an interval to check for changes in 'editorText' every 5 seconds
      const interval = setInterval(() => {
        // If less than or equal to 5 seconds have passed since the last save
        // and 'editorText' has changed since the last save
        if (
          currentTime - lastSavedTime <= 5000 &&
          prevEditorTextRef.current !== editorText
        ) {
          const url = constants.base_url + "/evalpro/editor/" + programId;
          const form_data = new FormData();
          form_data.append("buffer_data", JSON.stringify(editorText));
          form_data.append("session_key", sessionKey);
          httpService
            .put(url, form_data, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              if (response.data.status === "code_changed") {
                setShowEditorPopup(true);
              }
              prevEditorTextRef.current = editorText;
            });

          // Update 'lastSavedTime' with the current time
          setLastSavedTime(currentTime);
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [editorText]);

  // This useEffect hook runs whenever the 'selectedPopUpSubmission' state changes
  useEffect(() => {
    if (selectedPopUpSubmission) {
      // If the submission contains more than one file or is a .zip file
      if (
        selectedPopUpSubmission?.names_of_files_submitted.length > 1 ||
        selectedPopUpSubmission?.file_submitted.endsWith(".zip")
      ) {
        const zipUrl =
          constants.base_url +
          "/" +
          selectedPopUpSubmission?.file_submitted.toString().substr(1);

        httpService
          .get(zipUrl, {
            responseType: "arraybuffer",
          })
          .then((res) => {
            // Use JSZip to read the .zip file
            JSZip.loadAsync(res.data).then((zip) => {
              // Create a copy of the 'editorText' state
              const updatedEditorText = { ...editorText };

              // Create an array to store promises for reading each file in the .zip
              const promises = [];

              // Iterate over each file in the .zip
              zip.forEach((relativePath, file) => {
                // Create a new promise for reading the file
                promises.push(
                  new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = function (event) {
                      // Get the file name without the extension
                      const fileNameWithoutExtension = relativePath.endsWith(
                        ".h"
                      )
                        ? relativePath
                        : relativePath.substring(
                            0,
                            relativePath.lastIndexOf(".")
                          );

                      // Add the file content to 'updatedEditorText'
                      updatedEditorText[fileNameWithoutExtension] =
                        event.target.result;

                      // Resolve the promise
                      resolve();
                    };

                    // Read the file as text
                    file.async("arraybuffer").then((data) => {
                      reader.readAsText(new Blob([data]));
                    });
                  })
                );
              });

              // When all files have been read
              Promise.all(promises).then(() => {
                if (
                  //This is to ensure that data from buffer is loaded on the opening the activity.
                  //If the deadline is passed, then the code form latest submission will be loaded.
                  //if the user has uploaded a file, then the file content will be immediately loaded to the editor
                  !isFirstPageLoad ||
                  !access.enable_upload_submission ||
                  fileWasUploaded
                ) {
                  setEditorText(updatedEditorText);
                }

                setLanguage(
                  selectedPopUpSubmission.submission_program_language
                );
              });

              setIsFirstPageLoad(false);
              setFileWasUploaded(false);
            });
          })
          .catch((error) => {
            log.error(error);
          });
      } else {
        // If the submission contains only one file and is not a .zip file
        const url =
          constants.base_url +
          "/" +
          selectedPopUpSubmission?.file_submitted.toString().substr(1);
        httpService
          .get(url, {
            headers: {
              "Content-Type": "application/pdf",
            },
          })
          .then((res) => {
            // Get the file name without the extension
            const fileName =
              selectedPopUpSubmission.names_of_files_submitted[0];
            const fileNameWithoutExtension = fileName.substring(
              0,
              fileName.lastIndexOf(".")
            );
            //This is to ensure that data from buffer is loaded on the opening the activity.
            //If the deadline is passed, then the code form latest submission will be loaded.
            //if the user has uploaded a file, then the file content will be immediately loaded to the editor
            if (
              !isFirstPageLoad ||
              !access.enable_upload_submission ||
              fileWasUploaded
            ) {
              // Update the 'editorText' state with the file content
              setEditorText({ [fileNameWithoutExtension]: res.data });
            }
            setLanguage(selectedPopUpSubmission.submission_program_language);

            setIsFirstPageLoad(false);
            setFileWasUploaded(false);
          })
          .catch((error) => {
            // Log any errors
            log.error(error);
          });
      }
    }
  }, [selectedPopUpSubmission]);

  const modeHandler = () => {
    if (mode === 'light') {
      setMode('dark');
    } else {
      setMode('light');
    }
  }

  //Whenever there is a discrepancy in the editor text and the buffer data, a popup will be shown to the student with two options:
  //1. Load the changes from the buffer data
  //2. Discard the changes in the buffer data and continue with the current editor text
  //This function is called when the student clicks on the 'Load Changes' Button in the popup

  const handleLoadChanges = () => {
    const url =
      constants.base_url + "/evalpro/editor/load_changes/" + programId;
    const form_data = new FormData();
    form_data.append("session_key", sessionKey);
    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setEditorText(response.data.editor_buffer.buffer_data);
      });
    // Close the popup
    setShowEditorPopup(false);
  };

  //This function is called when the student clicks on the 'Discard Changes' Button in the popup
  const handleDiscardChanges = async () => {
    const url = constants.base_url + "/evalpro/editor/discard/" + programId;
    const form_data = new FormData();
    form_data.append("buffer_data", JSON.stringify(editorText));
    form_data.append("session_key", sessionKey);
    await httpService.put(url, form_data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    // Close the popup
    setShowEditorPopup(false);
  };

  const getFormattedDate = (date, type) => {
    if (type) {
      return new Date(date).toLocaleString();
    } else {
      const dateParts = new Date(date).toLocaleString().split(","); // Splitting the formatted date

      // return formatted date
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{dateParts[1]}</div>
          <div>{dateParts[0]}</div>
        </div>
      );
    }
  };

  const getSubmissionDate = () => {
    // return submission time if there is submission else ""
    if (selectedPopUpSubmission) {
      return getFormattedDate(selectedPopUpSubmission?.uploaded_on);
    } else {
      return "";
    }
  };

  const getDownloadPath = () => {
    // download path for selected submission
    return (
      constants.base_url + selectedPopUpSubmission?.file_submitted.toString()
    );
  };

  const executionStatus = (button) => {
    // To check the status of submission execution for seleted submission
    let url = null;
    let form_data = new FormData();

    /**This Button can hold three values - visible, hidden and null where null is the case when this executionStatus function
     * is called from RunCustomInput, so based on that setting the URL for getting the latest execution status
     **/
    if (button) {
      url =
        constants.base_url +
        "/evalpro/" +
        button +
        "/status/" +
        selectedPopUpSubmission.id;
    } else {
      url =
        constants.base_url +
        "/evalpro/custom_input_status/" +
        selectedPopUpSubmission.id;
      form_data.append("task_id", customResult?.task_id);
      form_data.append("input_file_name", customResult?.input_file_name);
    }
    // meta data for the axios requestExpected_Submission_File
    const _requestMetadata = {
      method: "PUT",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        setEvaluationStatus(response.data.currentStatus);
        // if evaluation finished setting the flag as "Evalution finsihed" to stop sending axios request and get the execution results
        if (response.data.stop) {
          evaluate_flag = "Evaluation Finished";

          /**This Button can hold three values - visible, hidden and null where null is the case when this executionStatus function
           *  is called from RunCustomInput, so based on that calling runTestCaseButton or RunCustomInput for getting the latest execution result
           **/
          if (button) {
            runTestCaseButton(button);
          } else {
            clearInterval(interval);
            evaluate_flag = "Evaluation in Process"; //Reseting it for upcoming click on Run Button i.e. fresh request

            setEvaluationStatus(null); //Reseting it for upcoming click on Run Button i.e. fresh request
            setCustomResult(response?.data?.result);
            //This flag is used to render the Evaluation Status for asychronous execution; setting it false to unhide the component
            setIsFetchingCustom(false);
          }
          displayGlobalMessage(response.data.currentStatus);
          if (response.data.result.status?.toLowerCase() == "pass") {
            displayGlobalMessage("Pass", "success");
          } else if (response.data.result.status?.toLowerCase() == "fail") {
            displayGlobalMessage("Fail", "error");
          }
        }
      })
      .catch((error) => {
        log.error("run ", "failed. error: ", error);
      });
  };

  const RunCustomInput = () => {
    // for custom input evaluation
    if (!studentProgramming.selected_submission) {
      displayGlobalMessage("Make a submission First....");
    } else {
      // This flag is used to render the Evaluation Status for asynchronous execution
      setIsFetchingCustom(true);

      const url =
        constants.base_url +
        "/evalpro/run_custom_input/" +
        selectedPopUpSubmission.id;
      let form_data = new FormData();
      form_data.append("input_text", customInput);
      form_data.append("key", "buffer_data");
      httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setCustomResult(response?.data?.result);
          customResult = response?.data?.result;
        })
        .then(() => {
          if (evaluationStatus === null) {
            setEvaluationStatus("Submission waiting in the queue");
          }

          if (evaluate_flag === "Evaluation in Process") {
            interval = setInterval(() => {
              // time interval between sending evaluation requests 2000ms = 2sec
              executionStatus(null);
            }, 2000);
          }
        })

        .catch((error) => {});
    }
  };

  const runTestCaseButton = (button) => {
    // if true loading icon will come up and also used to show execution Status
    if (button === "visible") setIsFetchingVisible(true);
    else if (button === "hidden") setIsFetchingHidden(true);

    const url =
      constants.base_url +
      "/evalpro/evaluate/" +
      button +
      "/" +
      selectedPopUpSubmission.id;

    let form_data = new FormData();

    httpService
      .put(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // evaluationStatus == null means evaluate Button has not been clicked so, if the first response is coming that means
        // Submission(Task) has been queued in the celery queue
        if (evaluationStatus === null) {
          setEvaluationStatus("Submission waiting in the queue");
        }

        if (evaluate_flag === "Evaluation in Process") {
          interval = setInterval(() => {
            // time interval between sending evaluation requests 2000ms = 2sec
            executionStatus(button);
          }, 2000);
        }

        if (evaluate_flag === "Evaluation Finished") {
          clearInterval(interval);
          evaluate_flag = "Evaluation in Process"; // Resetting it for the upcoming click on the Run Button i.e. fresh request
          setEvaluationStatus(null); // Resetting it for the upcoming click on the Run Button i.e. fresh request
          if (button === "hidden") {
            setObtainedHiddenMarks(response.data.obtained_marks);
            setTimeout(()=>{
              evaluateMarks?.current?.click();
              log.log("evaluate marks", evaluateMarks)
            },100)
          }
          if (button === "visible") {
            setObtainedVisibleMarks(response.data.obtained_marks);
            setTimeout(()=>{
              practiceMarks?.current?.click();
              log.log("practice marks", practiceMarks)
            },100)
          }
          changeAccess(response.data.access_control_vars);
          setIsFetchingHidden(false);
          setIsFetchingVisible(false);
          setSelectedSubmission(selectedPopUpSubmission);
          setCompilerErrors(response.data.testcase_details[0]?.compiler_errors);
        }
      })
      .catch((error) => {
        setIsFetchingVisible(false);
        setIsFetchingHidden(false);
      });
  };

  // function for fetching previous all submissions

  const fetchPreviousSubmissions = (e) => {
    setShowPopupSubmissions(true);
    var url = constants.base_url + "/evalpro/my_submissions/" + programId;
    httpService
      .get(url)
      .then((response) => {
        var count = Math.ceil(response.data.submissions.count / sizePage);
        setSubmissions(response.data.submissions.results);
        setPageCount(count);
      })
      .catch((error) => {});
  };

  const paginationHandler = (data) => {
    let currPageNum = data.selected + 1;

    var url =
      constants.base_url +
      "/evalpro/my_submissions/" +
      programId +
      "?page=" +
      currPageNum;
    httpService
      .get(url)
      .then((response) => {
        var count = Math.ceil(response.data.submissions.count / sizePage);
        setSubmissions(response.data.submissions.results);
        setPageCount(count);
      })
      .catch((error) => {
        log.error(error);
      });
  };

  // Function to handle file upload
  const onFileUpload = async (e) => {
    e.preventDefault();

    // Get the selected files
    const files = selectedFiles;

    // If no files are selected, display an error message and return
    if (files.length === 0) {
      displayGlobalMessage("No files selected", "error");
      return;
    }

    const form_data = new FormData();

    // Iterate over the selected files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // Set the initial states for file size, upload progress, and upload response
      setFileSize(true);
      setFileUploadProgress(true);
      setFileUploadResponse(null);

      // If the file is empty, ask the user if they want to continue
      if (file.size === 0) {
        const flag = window.confirm(
          "The uploaded file is empty. Do you still want to continue?"
        );
        if (!flag) {
          return;
        }
      }

      // If the file size is greater than 20480 bytes, set the states and return
      if (file.size > 20480) {
        setFileSize(false);
        setFileUploadProgress(false);
        setFileUploadResponse(null);
        return;
      }

      // Append the file and its name to the form data
      form_data.append("buffer_data", JSON.stringify(editorText));
      form_data.append("session_key", sessionKey);
      form_data.append("file_submitted", file);
      form_data.append("names_of_files_submitted", file.name);
    }
    const url = constants.base_url + "/evalpro/upload_template/" + programId;
    try {
      const response = await httpService.post(url, form_data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
  
      // On successful upload, update the access control variables,
      // selected submission, and other states
      changeAccess(response.data.access_control_vars);
      dispatch(updateSelectedSubmission(response.data.student_submission.id));
      setFileUploadResponse("Upload Successful");
      setFileWasUploaded(true);
      setSelectedPopUpSubmission(response.data.student_submission);
      setObtainedHiddenMarks(null);
      setObtainedVisibleMarks(null);
      setTestResults(null);
      setShowStudentPreview(true);
      displayGlobalMessage("Submission uploaded Successfully", "success");
      setSelectedFiles([]);
    } catch (error) {
      // On error, set the upload response state to the error message
      setFileUploadResponse(error?.response?.data?.detail);
    }

    // Reset the upload progress and compiler errors states
    setFileUploadProgress(false);
    setCompilerErrors(null);
  };

  //Function to handle run without submit
  const onRunCode = async (e) => {
    try {
      setIsRunLoading(true);
      await handleDiscardChanges();
      setRunToggle(!runToggle);
      const url =
        constants.base_url +
        "/evalpro/editor/check_compilation_status/" +
        programId;
      const form_data = new FormData();
      form_data.append("programming_language", language);
      form_data.append("key", "buffer_data");
      const response = await httpService
        .put(url, form_data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

      if (response.data.result.status === "fail") {
        setErrorOnRun(response.data.result.error);
        setSuccessOnRun(false);
      } else {
        setErrorOnRun("");
        setSuccessOnRun(true);
      }
      setIsRunLoading(false);

    } catch (error) {
      log.error(error);
    } finally {
      setIsRunLoading(false);
    }
  };

  //Function to handle submit Button in the editor
  const onSubmitCode = (e) => {
    let combinedContent = "";
    const files = Object.keys(editorText).map((fileName) => {
      const content = editorText[fileName];
      combinedContent += `File: ${fileName}.${languageToFileExtension[language]}\n${content}\n\n`;
      //log.log("combined content:", combinedContent)
      const myBlob = new Blob([content], { type: "text/plain" });
      let fileNameWithExt = fileName.endsWith(".h")
        ? fileName
        : `${fileName}.${languageToFileExtension[language]}`;
      return new File([myBlob], fileNameWithExt, { type: myBlob.type });
    });
    
    const arr = Object.keys(instructorAdditionalFilesContent[language]);
    arr.forEach(filename => {
      const content = instructorAdditionalFilesContent[language][filename]
      combinedContent += `File: ${filename}\n${content}\n\n`;
    });
    
    //setStudentPreviewCode(combinedContent);
    setSelectedFiles(files);
    selectedFiles = files;
    onFileUpload(e);
  };

  //Function which is called when the Editor is mounted
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor; // Store the editor instance in the ref
  };

  // This function is used to set the selected submission from a list of submissions.
  const SetSelectedSubmission = (event) => {
    // Get all elements with the name "submissions"
    var ele = document.getElementsByName("submissions");
    // Initialize submission id and checked element value
    var sub_id = -1;
    var checked_ele_value = -1;
    // Loop through all elements to find the checked one
    for (let i = 0; i < ele.length; i++) {
      // If the element is checked, get its value and the corresponding submission id
      if (ele[i].checked) {
        checked_ele_value = ele[i].value;
        sub_id = submissions[checked_ele_value]["id"];
        break;
      }
    }

    // Construct the URL for the PUT request
    let url =
      constants.base_url +
      "/evalpro/select_submission_for_evaluation/" +
      sub_id;

    // Make a PUT request to the server
    httpService
      .put(url, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        // This code will execute after the PUT request is successful
        // Update the selected submission in the state
        dispatch(updateSelectedSubmission(submissions[checked_ele_value].id));
        setSelectedPopUpSubmission(submissions[checked_ele_value]);
        setSelectedSubmission(submissions[checked_ele_value]);
        setShowPopupSubmissions(false);

        // Check if execution is pending for visible and hidden test cases, and set marks accordingly
        if (
          !submissions[checked_ele_value]
            .execution_pending_for_visible_testcases
        ) {
          setObtainedVisibleMarks(
            submissions[checked_ele_value].obtained_visible_marks
          );
        } else {
          setObtainedVisibleMarks(null);
        }

        if (
          !submissions[checked_ele_value].execution_pending_for_hidden_testcases
        ) {
          setObtainedHiddenMarks(
            submissions[checked_ele_value].obtained_hidden_marks
          );
        } else {
          setObtainedHiddenMarks(null);
        }

        // Reset custom result
        setCustomResult(null);

        // Construct the URL for the GET request
        var latesturl = constants.base_url + "/evalpro/get_latest/" + programId;

        // Make a GET request to the server
        httpService
          .get(latesturl)
          .then((response) => {
            // Update access control variables and compiler errors in the state
            changeAccess(response.data.access_control_vars);
            setCompilerErrors(response.data.student_submission.compiler_errors);
          })
          .catch((error) => {
            // Log any errors from the GET request
            log.error(error);
          });
      })
      .catch((error) => {
        // Handle any errors from the PUT request
      });
  };

  // This function is triggered when files are dropped into the dropzone.
  const onDrop = (acceptedFiles) => {
    // Filter out the files that are already selected.
    // This prevents duplicate files from being selected.
    const newFiles = acceptedFiles.filter(
      (file) =>
        // The 'some' method tests whether at least one element in the array passes the test implemented by the provided function.
        // In this case, it checks if the file is not already in the selectedFiles array by comparing their names.
        !selectedFiles.some((selectedFile) => selectedFile.name === file.name)
    );
    // Update the selectedFiles state with the new files.
    // The spread operator (...) is used to create a new array that contains the values of the old array (selectedFiles) and the new array (newFiles).
    setSelectedFiles([...selectedFiles, ...newFiles]);
  };

  // Function to remove a file from the selected files
  const removeFile = (fileIndex) => {
    // Create a new array from the current selected files
    const newSelectedFiles = [...selectedFiles];
    // Remove the file at the specified index
    newSelectedFiles.splice(fileIndex, 1);
    // Update the state with the new array of selected files
    setSelectedFiles(newSelectedFiles);
  };

  // Function to handle changes in the editor
  const handleEditorChange = (value) => {
    // If ignoreChange is true, reset it to false and return immediately. This is to prevent an infinite loop when setting the editor value.
    if (ignoreChange.current) {
      ignoreChange.current = false;
      return;
    }
    // If the file is an instructor-provided file, display a message and prevent editing
    if (isInstructorFile) {
      displayGlobalMessage(
        "You are viewing an instructor-provided file. Editing is not allowed.",
        "info"
      );
      // If the editor reference is valid, reset the editor content to the original content
      if (editorRef.current) {
        ignoreChange.current = true;
        editorRef.current.setValue(
          instructorAdditionalFilesContent[language][currentFileinEditor]
        );
      }
      return;
    }
    // If the file is not an instructor-provided file, update the editor text state with the new value
    const updatedEditorText = { ...editorText };
    updatedEditorText[currentFileinEditor] = value;
    setEditorText(updatedEditorText);
  };

  return (
    <div>
      {/* Heading for the file upload option */}
      <h6 style={{ marginTop: "5px" }}>
        <u>Option 1: Upload Your Code File</u>
      </h6>

      {/* Display the allowed submission file names */}
      <div style={{ padding: "5px 0px" }}>
        Allowed Submission File Name(s):{" "}
        <b>
          <i>{Expected_Submission_File}</i>
        </b>
      </div>

      {/* Container for the file upload and upload Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingRight: "24px",
        }}
      >
        <div>
          {/* If file upload is enabled, display the Dropzone component for file upload */}
          {access.enable_upload_submission ? (
            <Dropzone onDrop={onDrop}>
              {({ getRootProps, getInputProps }) => (
                <section className="wfp--dropzone">
                  <div {...getRootProps({ className: "wfp--dropzone__input" })}>
                    <input {...getInputProps()} />
                    <div>
                      <Button variant="secondary">Choose File</Button>
                    </div>
                  </div>

                  {/* List of selected files */}
                  <ul style={{ paddingInlineStart: "0" }}>
                    {selectedFiles?.map((file, index) => (
                      <li
                        key={index}
                        style={{
                          marginBottom: "4px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "2px",
                          background: "#edf0f2",
                          borderRadius: "4px",
                        }}
                      >
                        {/* Display the file name and a Button to remove the file */}
                        <span>{file?.name}</span>{" "}
                        <Button
                          variant="danger"
                          onClick={() => removeFile(index)}
                          style={{ padding: "0px 6px", borderRadius: "4px" }}
                        >
                          x
                        </Button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </Dropzone>
          ) : (
            /* If file upload is not enabled, display a disabled Button with a tooltip */
            <OverlayTrigger
              id="ChooseFile"
              overlay={<Tooltip>Deadline passed</Tooltip>}
            >
              <Button variant="secondary" disabled>
                Choose File
              </Button>
            </OverlayTrigger>
          )}
        </div>

        {/* Upload Button */}
        <div>
          {access.enable_upload_submission ? (
            <Button variant="primary" onClick={onFileUpload} id={1}>
              Upload
            </Button>
          ) : (
            <Button style={{ marginBottom: "16px" }} disabled>
              Upload
            </Button>
          )}
        </div>
      </div>

      {/* Alert for file size exceeded */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Alert
          show={!fileSize}
          variant="danger"
          style={{ marginBottom: "10px" }}
          onClose={() => setFileSize(true)}
          dismissible
        >
          File size exceeded!!
        </Alert>
      </div>

      {/* Alert for file upload response */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {fileUploadResponse !== null && (
          <Alert
            show={true}
            variant={
              fileUploadResponse === "Upload Successful" ? "success" : "danger"
            }
            style={{ marginBottom: "10px" }}
            onClose={() => setFileUploadResponse(null)}
            dismissible
          >
            {fileUploadResponse}
          </Alert>
        )}
      </div>

      {/* Horizontal line */}
      <hr style={{ marginTop: "0" }} />

      {/* Integrated Code editor start*/}
      {/* Heading for the code writing and submission option */}
      <h6>
        <u>Option 2: Write and Submit Your Code</u>
      </h6>

      {/* Container for the integrated code editor */}
      <div
        id="Integrated code editor"
        style={{
          border: "3px solid #b5b3b3", // Border styling
          borderRadius: "5px", // Rounded corners
          padding: "8px 8px 0px 8px", // Padding
        }}
      >
        {/* Modal for handling buffer changes */}
        <Modal
          dialogClassName="modal-70w" // Custom class for modal
          show={showEditorPopup} // Show modal when buffer changes
          backdrop="static" // Disables closing the modal by clicking outside of it
          keyboard={false} // Disables closing the modal with the escape key
          centered // Centers the modal vertically in the viewport
        >
          <Modal.Header>
            {/* Modal title */}
            <Modal.Title>Buffer Changed!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Modal body text */}
            Code in buffer has changed. Do you want to load changes from the
            buffer or keep your current code?
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            {/* Button to load changes from the buffer */}
            <Button variant="primary" onClick={handleLoadChanges}>
              Load Changes
            </Button>
            {/* Button to keep the current code */}
            <Button variant="secondary" onClick={handleDiscardChanges}>
              Keep Current Code
            </Button>
          </Modal.Footer>
        </Modal>

        {/* FullScreen component for the code editor */}
        <FullScreen
          handle={handle}
          onChange={(state) => setisFullScreen(state)}
          style={{ backgroundColor: "white" }}
        >
          <div style={{ backgroundColor: "white" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingBottom: "8px",
                padding: isFullScreen ? "8px" : "0px 0px 8px 0px", // Conditional padding based on fullscreen mode
              }}
            >
              <ButtonGroup>
                {allFiles.map((key, index) => {
                  const isUserFile =
                    submission.files_to_be_submitted.includes(key);
                  const isHeaderFileToBeSubmitted =
                    headerFileToBeSubmitted.includes(key);
                  const fileName = isUserFile
                    ? `${key}.${languageToFileExtension[language]}`
                    : key;
                  const isSelected = key === currentFileinEditor;

                  return (
                    <Button
                      key={index}
                      variant={
                        isSelected
                          ? isUserFile || isHeaderFileToBeSubmitted
                            ? "secondary"
                            : "info"
                          : isUserFile || isHeaderFileToBeSubmitted
                            ? "outline-secondary"
                            : "outline-info"
                      } // Use 'secondary' if selected, otherwise 'outline-secondary'
                      size="sm"
                      onClick={() => {
                        setCurrentFileinEditor(key);
                        setIsInstructorFile(
                          !isUserFile && !isHeaderFileToBeSubmitted
                        );
                      }}
                    >
                      {fileName}
                    </Button>
                  );
                })}
              </ButtonGroup>
              <div
                style={{
                  display: "flex",
                  marginLeft: "auto",
                  paddingRight: "1rem",
                  alignItems: "center",
                }}
              >
                <div style={{ marginRight: "5px" }}>
                  <OverlayTrigger
                    overlay={<Tooltip>Change Mode</Tooltip>}
                  >
                    <Button 
                      variant="secondary"
                      onClick={() => modeHandler()}
                    >
                      {mode==='light' ? (
                        <MdLightMode />
                      ) : (
                        <MdDarkMode />
                      )}
                    </Button>
                  </OverlayTrigger>&nbsp;
                  <OverlayTrigger
                    overlay={<Tooltip>Toggle Fullscreen</Tooltip>}
                  >
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (isFullScreen) {
                          handle.exit();
                        } else {
                          handle.enter();
                        }
                      }}
                    >
                      {isFullScreen ? (
                        <MdOutlineFullscreenExit />
                      ) : (
                        <MdOutlineFullscreen />
                      )}
                    </Button>
                  </OverlayTrigger>{" "}
                  <OverlayTrigger overlay={<Tooltip>Zoom In</Tooltip>}>
                    <Button
                      variant="secondary"
                      onClick={() => setFontSize(fontSize + 1)}
                    >
                      <MdOutlineZoomIn />
                    </Button>
                  </OverlayTrigger>{" "}
                  <OverlayTrigger overlay={<Tooltip>Zoom Out</Tooltip>}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (fontSize > 1) setFontSize(fontSize - 1);
                      }}
                    >
                      <MdOutlineZoomOut />
                    </Button>
                  </OverlayTrigger>{" "}
                </div>
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm">
                    {`Language: ${language}`}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {submission?.program_language.map((lang, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={() => {
                          setLanguage(lang);
                          const userFiles =
                            submission.files_to_be_submitted.filter(
                              (fileName) =>
                                !instructorAdditionalFilesContent[
                                  lang
                                ]?.hasOwnProperty(fileName)
                            );
                          setCurrentFileinEditor(userFiles[0]);
                          setIsInstructorFile(false);
                          // Set the first user file when language changes
                        }}
                      >
                        {lang}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            <div
              style={{
                border: "2px solid #ccc",
                borderRadius: "5px",
                padding: "8px",
                marginBottom: "8px",
              }}
            >
              <MonacoEditorComponent
                height={isFullScreen ? "95vh" : "50vh"}
                language={languageToSyntaxHighlighting[language]}
                path={currentFileinEditor}
                value={
                  isInstructorFile
                    ? instructorAdditionalFilesContent[language][
                    currentFileinEditor
                    ]
                    : editorText[currentFileinEditor]
                }
                onChange={handleEditorChange}
                options={{
                  fontSize: fontSize,
                }}
                onMount={handleEditorDidMount}
                theme={mode === 'light'?'light':'vs-dark'}
              />
            </div>
          </div>
        </FullScreen>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingRight: "1rem",
            paddingBottom: "-16px",
            height: "50px",
            flexFlow: "row",
            alignItems: "center",
          }}
        >
          {/* <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "10px",
                alignItems: "flex-end",
              }}
            >
              <OverlayTrigger
                id="enabledPreviousVersion"
                overlay={
                  <Tooltip>
                    {access.enable_select_previous_submission
                      ? "By default the latest submitted code is taken as final submission. Click here to change."
                      : "Disabled because  deadline passed or no submission"}
                  </Tooltip>
                }
              >
                {access.enable_select_previous_submission ? (
                  <Button
                    variant="secondary"
                    onClick={(e) => fetchPreviousSubmissions(e)}
                  >
                    <FaEdit size="20" /> Previous Submissions
                  </Button>
                ) : (
                  <Button variant="secondary" disabled>
                    <FaEdit size="20" /> Previous Submissions
                  </Button>
                )}
              </OverlayTrigger>
              <p
                style={{ marginBottom: "0px", color: "#666", fontSize: "12px" }}
              >
                <i>
                  {lastSavedTime
                    ? "AutoSaved: " + lastSavedTime.toLocaleString()
                    : ""}
                </i>
              </p>
            </div>
            <div
              className="col-12"
              style={{ marginLeft: "250px", marginTop: "20px" }}
            >
              <Modal
                show={showPopupSubmissions}
                dialogClassName="modal-90w"
                onHide={() => {
                  setShowPopupSubmissions(false);
                }}
                size="xl"
                aria-labelledby="contained-modal-title-vcenter"
                centered
              >
                <Modal.Header closeButton>
                  <div style={{ color: "red", fontSize: "15px" }}>
                    Text in red :{" "}
                  </div>
                  <div style={{ paddingLeft: "10px" }}>
                    Indicates current submission
                  </div>
                  <Button
                    style={{ marginLeft: "300px" }}
                    onClick={(e) => SetSelectedSubmission()}
                  >
                    Select
                  </Button>
                </Modal.Header>
                <Modal.Body>
                  <div className="row">
                    <div className="col-1"></div>
                    <div className="col-2">Files</div>
                    <div className="col-3">Submitted at</div>
                    <div className="col-3">Practice Marks</div>
                  </div>
                  <hr />
                  {submissions?.map((item, i) => (
                    <div key={i}>
                      <div className="row">
                        <div className="col-1">
                          <input
                            type="radio"
                            name="submissions"
                            value={i}
                          ></input>
                        </div>

                        <div
                          className="col-2"
                          style={{
                            color:
                              selectedPopUpSubmission.id == item.id
                                ? "red"
                                : "inherit",
                          }}
                        >
                          {item.names_of_files_submitted.join(", ")}
                        </div>

                        <div className="col-3">
                          {" "}
                          {getFormattedDate(item.uploaded_on, true)}
                        </div>
                        {item.compiler_errors ? (
                          <div className="col-3">Compilation error</div>
                        ) : (
                          <div className="col-3">
                            {item.execution_pending_for_visible_testcases ? (
                              <div>Run to View Marks</div>
                            ) : (
                              <div className="col-3">
                                {item.obtained_visible_marks}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <br />
                    </div>
                  ))}
                </Modal.Body>

                <ReactPaginate
                  previousLabel={"Prev"} //just a label in UI for prev Button
                  nextLabel={"Next"} //just a label in UI for next Button
                  breakLabel={"..."} //just a label in UI for breaking the pagination labels
                  pageCount={pageCount} //count for total number of pages over which all labs are spanning
                  marginPagesDisplayed={2} //if it is '2' i.e. in the begin and last it will show to 2 page numbers e.g. (1) (2) ... (n-1) (n)
                  pageRangeDisplayed={3} //total page numbers is to be shown in middle after break (i.e after clicking on 3 dots)
                  onPageChange={paginationHandler} //function i.e. making an axios call to fetch a particular page based on the clicked number
                  containerClassName={"pagination justify-content-center"} //css class for styling, similarly all below className(s) are also for styling
                  pageClassName={"page-item"}
                  pageLinkClassName={"page-link"}
                  previousClassName={"page-item"}
                  previousLinkClassName={"page-link"}
                  nextClassName={"page-item"}
                  nextLinkClassName={"page-link"}
                  breakClassName={"page-item"}
                  breakLinkClassName={"page-link"}
                  activeClassName={"active"}
                />
              </Modal>
            </div>
          </div> */}

          <div>
            <OverlayTrigger overlay={<Tooltip>Run Without Submit</Tooltip>}>
              <Button
                variant="primary"
                onClick={onRunCode}
                style={{ marginRight: "1rem" }}
              >
                <VscRunAll />
              </Button>
            </OverlayTrigger>
            <Button
              variant="primary"
              onClick={onSubmitCode}
              disabled={!access.enable_upload_submission}
            >
              Submit Code
            </Button>
          </div>
        </div>
        <RunTerminal
          errorOnRun={errorOnRun}
          programId={programId}
          language={language}
          runToggle={runToggle}
          successOnRun={successOnRun}
          setRunToggle={setRunToggle}
          setErrorOnRun={setErrorOnRun}
          setSuccessOnRun={setSuccessOnRun}
          isRunLoading={isRunLoading}
        />
      </div>
      {/* Integrated Code editor end*/}

      <div id="Result row">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingBottom: "8px",
            paddingTop: "10px",
            paddingRight: "1rem",
          }}
        >
          <div
            id="File Submitted"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <h6 style={{ fontSize: "15px", fontWeight: "bold" }}>
              {" "}
              File submitted
            </h6>
            <div id="Download file submitted">
              <a href={getDownloadPath()} download>
                {selectedPopUpSubmission?.names_of_files_submitted.map(
                  (fileName, index) => (
                    <div key={index}>{fileName}</div>
                  )
                )}
              </a>
            </div>
          </div>

          <div
            id="Submission date"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <h6 style={{ fontSize: "15px", fontWeight: "bold" }}>
              Time of submission
            </h6>
            <div>{getSubmissionDate()}</div>
          </div>

          <div
            id="Visible marks"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <h6 style={{ fontSize: "15px", fontWeight: "bold" }}>
              Practice marks
            </h6>
            <div id="Visible marks button">
              {!access.enable_show_visible_marks ? (
                <div>
                  {access.enable_run_visible_testcases &&
                    isFetchingVisible === false ? (
                    <div>
                      {compilerErrors ? (
                        <Button
                          variant="link"
                          onClick={() => setShowPopupCompilerError(true)}
                          style={{
                            color: "red",
                          }}
                        >
                          <u>Compilation Error</u>
                        </Button>
                      ) : (
                        <OverlayTrigger overlay={<Tooltip>Run</Tooltip>}>
                          <Button
                            variant="primary"
                            id="Run1"
                            onClick={(e) => runTestCaseButton("visible")}
                          >
                            <AiOutlinePlayCircle />
                          </Button>
                        </OverlayTrigger>
                      )}
                    </div>
                  ) : (
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          {isFetchingVisible
                            ? "Submission running on Visible testcases"
                            : submission.visible_testcases_count
                              ? "Make atleast one submission to Run"
                              : "No visible testcases"}
                        </Tooltip>
                      }
                    >
                      <div>
                        <Button variant="primary" disabled>
                          <AiOutlinePlayCircle />
                        </Button>
                      </div>
                    </OverlayTrigger>
                  )}

                  {isFetchingVisible && (
                    <p style={{ color: "red" }}>
                      <AiOutlineRetweet size="20" />
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  {obtainedVisibleMarks}
                </div>
              )}
            </div>
          </div>

          <div
            id="Evaluate marks"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <h6 style={{ fontSize: "15px", fontWeight: "bold" }}>
              Evaluate marks
            </h6>
            <div id="Evaluate marks button">
              {access.enable_show_hidden_marks === false ? (
                <div>
                  <div>
                    {access.enable_run_hidden_testcases &&
                      isFetchingHidden === false ? (
                      <div>
                        {compilerErrors ? (
                          <Button
                            variant="link"
                            onClick={() => setShowPopupCompilerError(true)}
                            style={{
                              color: "red",
                            }}
                          >
                            <u>Compilation Error</u>
                          </Button>
                        ) : (
                          <OverlayTrigger overlay={<Tooltip>Run</Tooltip>}>
                            <Button
                              id="Run2"
                              variant="primary"
                              onClick={(e) => runTestCaseButton("hidden")}
                            >
                              <AiOutlinePlayCircle size="20" />
                            </Button>
                          </OverlayTrigger>
                        )}
                      </div>
                    ) : (
                      <OverlayTrigger
                        overlay={
                          <Tooltip>
                            {isFetchingHidden
                              ? "Submission running on Hidden testcases"
                              : submission.hidden_testcases_count
                                ? "You can run your submission on Evaluate testcases only after deadline has been passed"
                                : "No hidden testcases"}
                          </Tooltip>
                        }
                      >
                        <div>
                          <Button variant="outline-secondary" disabled>
                            <AiOutlinePlayCircle size="20" />
                          </Button>
                        </div>
                      </OverlayTrigger>
                    )}
                  </div>
                  <div style={{ marginLeft: "0px" }}>
                    {isFetchingHidden && (
                      <p style={{ color: "red" }}>
                        <AiOutlineRetweet size="20" />
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  {obtainedHiddenMarks}
                </div>
              )}
            </div>
          </div>

          <div
            id="Custom input"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <h6 style={{ fontSize: "15px", fontWeight: "bold" }}>
              Custom Input
            </h6>
            <div id="Custom input button">
              <Button
                onClick={() => {
                  setShowCustomInputCard(!showCustomInputCard);
                }}
              >
                {showCustomInputCard === false ? (
                  <div>
                    <FiSettings size="20" />
                  </div>
                ) : (
                  <div>
                    <FiMinus size="20" />
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div>
        {/* Showing the Evaluation Status while evaluation started but not finished */}
        {isFetchingVisible || isFetchingHidden ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {evaluationStatus != null ? (
              <p style={{ color: "red", marginTop: "-30px" }}>
                {evaluationStatus}
              </p>
            ) : (
              <div></div>
            )}
          </div>
        ) : (
          <div></div>
        )}
      </div>
      <div
        id="Feedback"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {selectedPopUpSubmission?.comments ? (
          <Alert style={{ padding: "0px" }} variant="secondary">
            Feedback: {selectedPopUpSubmission.comments}
          </Alert>
        ) : (
          ""
        )}
      </div>

      {/* Modal for displaying compiler errors */}
      <Modal
        show={showPopupCompilerError} // Show modal when there are compiler errors
        dialogClassName="modal-90w" // Custom class for modal
        onHide={() => setShowPopupCompilerError(false)} // Hide modal when the close Button is clicked
        size="xl" // Extra large modal
        aria-labelledby="contained-modal-title-vcenter" // Accessibility label
        centered // Centers the modal vertically in the viewport
      >
        <Modal.Header closeButton>Compilation Error</Modal.Header>{" "}
        {/* Modal title */}
        <Modal.Body>
          <div className="row">
            <div className="col-12 col1">
              {/* Textarea for displaying the compiler errors */}
              <textarea
                className="textarea1" // Custom class for textarea
                value={compilerErrors} // Display the compiler errors
                style={{
                  color: "red", // Red text
                  backgroundColor: "black", // Black background
                  width: "100%", // Full width
                  height: "200px", // Fixed height
                }}
                disabled // Disable user input
              ></textarea>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Card>
        {/* Show the custom input card if the flag is set to true */}
        {showCustomInputCard === true ? (
          <Card.Body>
            {/* Header row for input and output */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <b>INPUT</b>
              </div>
              <div>
                <b>OUTPUT</b>
              </div>
              <div>
                <b></b>
              </div>
            </div>

            {/* Row for input and output textareas */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                {/* Textarea for entering custom input */}
                <textarea
                  className="textarea6"
                  style={{
                    backgroundColor: "black",
                    height: "100px",
                    width: "200px",
                  }}
                  placeholder="Enter your input"
                  onChange={(e) => setCustomInput(e.target.value)}
                ></textarea>
              </div>
              <div>
                {/* Textarea for displaying the output */}
                <textarea
                  className="textarea6"
                  style={{
                    backgroundColor: "black",
                    height: "100px",
                    width: "200px",
                  }}
                  value={customResult?.output || ""} // Display the output or an empty string
                  placeholder="output"
                  disabled // Disable user input
                ></textarea>
              </div>

              {/* Error message */}
              <div id="Custom input error">
                <OverlayTrigger overlay={<Tooltip>Click to Expand</Tooltip>}>
                  <a
                    onClick={() => setShowPopupCustomInput(true)}
                    style={{
                      color: "red",
                    }}
                  >
                    {/* Show 'Error' if there are runtime or compilation errors */}
                    {customResult?.runtime_errors ||
                      customResult?.compilation_errors ? (
                      <u>Error</u>
                    ) : (
                      ""
                    )}
                  </a>
                </OverlayTrigger>
              </div>
            </div>

            {/* Modal for displaying compiler and runtime errors */}
            <Modal
              show={showPopupCustomInput} // Show modal when the flag is set to true
              dialogClassName="modal-90w" // Custom class for modal
              onHide={() => setShowPopupCustomInput(false)} // Hide modal when the close Button is clicked
              size="xl" // Extra large modal
              aria-labelledby="contained-modal-title-vcenter" // Accessibility label
              centered // Centers the modal vertically in the viewport
            >
              <Modal.Header closeButton></Modal.Header>
              <Modal.Body>
                <div>Compiler Error</div>

                {/* Textarea for displaying the compiler errors */}
                <div className="row">
                  <div className="col-12 col1">
                    <textarea
                      className="textarea1"
                      value={customResult?.compilation_errors} // Display the compiler errors
                      style={{
                        color: "red",
                        backgroundColor: "black",
                        width: "100%",
                        height: "200px",
                      }}
                      disabled // Disable user input
                    ></textarea>
                  </div>
                </div>

                <div>Runtime Error</div>

                {/* Textarea for displaying the runtime errors */}
                <div className="row">
                  <div className="col-12 col1">
                    <textarea
                      className="textarea1"
                      value={customResult?.runtime_errors} // Display the runtime errors
                      style={{
                        color: "red",
                        backgroundColor: "black",
                        width: "100%",
                        height: "200px",
                      }}
                      disabled // Disable user input
                    ></textarea>
                  </div>
                </div>
              </Modal.Body>
            </Modal>

            {/* Button for running the submitted file */}
            <div className="row">
              <Button
                style={{ marginLeft: "14px" }}
                className="primary"
                onClick={RunCustomInput} // Run the custom input on click
              >
                Run Submitted File
              </Button>

              {/* Display the evaluation status while the evaluation is in progress */}
              <div className="col-2">
                {isFetchingCustom ? (
                  <div>
                    {evaluationStatus != null ? (
                      <p style={{ color: "red" }}>{evaluationStatus}</p> // Display the evaluation status
                    ) : (
                      <div></div>
                    )}
                  </div>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          </Card.Body>
        ) : null}
      </Card>
    </div>
  );
}